import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Modal from "../Modal";
import ModalCard from "../ModalCard";
import ModalActionBar from "shared/ModalActionBar";
import Input from "shared/@forms/Input";
import styles from "./AddYearModal.module.css";
import { ReactComponent as CancelButtonImg } from "../../assets/Edit_Category_Cancel.svg";
import { ReactComponent as AddButtonImg } from "../../assets/Edit_Category_Add.svg";

/**
 * Interactive component. Modal that opens when adding a new diamond reference document.
 */
export function AddYearModal({ isOpen, onClose, onAdd }) {
  const [year, setYear] = useState();
  const [touched, setTouched] = useState(false);

  const currentYear = new Date().getFullYear();
  const isValidYear = useMemo(() => {
    if (year == null || year === "") return false;
    const cleaned = String(year).trim();
    if (!/^\d{4}$/.test(cleaned)) return false; // must be exactly 4 digits
    const numeric = Number(cleaned);
    // Allow a reasonable historical lower bound and up to next year
    return numeric >= 1900 && numeric <= currentYear + 1;
  }, [year, currentYear]);

  useEffect(() => {
    if (!isOpen) {
      setYear("");
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        className={classNames(styles.container)}
        isOpen={isOpen}
        children={
          <>
            <div className={classNames(styles["button-bar"])}>
              <ModalActionBar
                children={
                  <>
                    <button type="button" onClick={onClose}>
                      <CancelButtonImg />
                    </button>
                    <button
                      type="button"
                      disabled={!isValidYear}
                      onClick={() => {
                        if (isValidYear) {
                          onAdd(year);
                        }
                      }}
                      style={{
                        opacity: !isValidYear ? 0.5 : 1,
                        cursor: !isValidYear ? "not-allowed" : "pointer",
                      }}
                    >
                      <AddButtonImg />
                    </button>
                  </>
                }
              />
            </div>
            <ModalCard
              children={
                <>
                  <h1 className={styles["year-label"]}>
                    Add a New Management Team Year
                  </h1>
                  <div className={classNames(styles["year-input"])}>
                    <Input
                      type="number"
                      name="year"
                      placeholder={"Year"}
                      value={year}
                      onChange={(key) => {
                        const value = key.target.value.replace(/[^0-9]/g, "");
                        if (value.length <= 4) {
                          setYear(value);
                        }
                      }}
                      onBlur={() => setTouched(true)}
                    />
                    {touched && !isValidYear && (
                      <div className={styles.errorMessage}>
                        Enter a 4-digit year between 1900 and {currentYear + 1}.
                      </div>
                    )}
                  </div>
                </>
              }
            />
          </>
        }
      />
    </>
  );
}

AddYearModal.propTypes = {
  /**
   * Boolean that determines if modal is displayed.
   */
  isOpen: PropTypes.bool,
  /**
   * Function that fires when the modal is closed.
   */
  onClose: PropTypes.func,
  /**
   * Function that fires when the add button is clicked.
   */
  onAdd: PropTypes.func,
};
